<template>
    <svg
        :class="['vue-star-rating-star', { 'vue-star-rating-star-rotate': shouldAnimate }]"
        :height="starSize"
        :width="starSize"
        :viewBox="viewBox"
        @mousemove="mouseMoving"
        @click="selected"
        @touchstart="touchStart"
        @touchend="touchEnd"
    >
        <linearGradient
            :id="grad"
            x1="0"
            x2="100%"
            y1="0"
            y2="0"
        >
            <stop
                :offset="starFill"
                :stop-color="(rtl) ? getColor(inactiveColor) : getColor(activeColor)"
                :stop-opacity="(rtl) ? getOpacity(inactiveColor) : getOpacity(activeColor)"
            />
            <stop
                :offset="starFill"
                :stop-color="(rtl) ? getColor(activeColor) : getColor(inactiveColor)"
                :stop-opacity="(rtl) ? getOpacity(activeColor) : getOpacity(inactiveColor)"
            />
        </linearGradient>

        <filter
            :id="glowId"
            height="130%"
            width="130%"
            filterUnits="userSpaceOnUse"
        >
            <feGaussianBlur
                :stdDeviation="glow"
                result="coloredBlur"
            />
            <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
            </feMerge>
        </filter>

        <polygon
            v-show="glowColor && glow > 0 && fill > 0"
            :points="starPointsToString"
            :fill="gradId"
            :stroke="glowColor"
            :filter="'url(#' + glowId + ')'"
            :stroke-width="border"
        />

        <polygon
            :points="starPointsToString"
            :fill="gradId"
            :stroke="getBorderColor"
            :stroke-width="border"
            :stroke-linejoin="strokeLinejoin"
        />
        <polygon
            :points="starPointsToString"
            :fill="gradId"
        />
    </svg>
</template>

<script lang="ts" setup>
    import { AlphaColor } from '#x1-base/utils';

    const props = withDefaults(
        defineProps<{
            fill?: number;
            points?: number[];
            size?: number;
            starId: number;
            activeColor: string;
            inactiveColor: string;
            borderColor?: string;
            activeBorderColor?: string;
            borderWidth?: number;
            roundedCorners?: boolean;
            rtl?: boolean;
            glow?: number;
            glowColor?: string;
            animate?: boolean;
        }>(),
        {
            fill: 0,
            points: () => [],
            size: 50,
            borderColor: '#000',
            activeBorderColor: '#000',
            borderWidth: 0,
            roundedCorners: false,
            rtl: false,
            glow: 0,
            animate: false
        }
    );

    const emit = defineEmits<{
        (e: 'star-mouse-move', value: { event: MouseEvent; position: number; id: number }): void
        (e: 'star-selected', value: { id: number; position: number }): void
    }>();

    const starPoints = ref<number[]>([19.8, 2.2, 6.6, 43.56, 39.6, 17.16, 0, 17.16, 33, 43.56]);
    const grad = ref('');
    const glowId = ref('');
    const isStarActive = ref(true);

    const starPointsToString = computed(() => {
        return starPoints.value.join(',');
    });

    const gradId = computed(() => {
        return 'url(#' + grad.value + ')';
    });

    const border = computed(() => {
        return (props.roundedCorners && props.borderWidth <= 0) ? 6 : props.borderWidth;
    });

    const starSize = computed(() => {
        // Adjust star size when rounded corners are set with no border, to account for the 'hidden' border
        const size = (props.roundedCorners && props.borderWidth <= 0) ? parseInt(String(props.size)) - parseInt(String(border.value)) : props.size;
        return parseInt(String(size)) + parseInt(String(border.value));
    });

    const starFill = computed(() => {
        return (props.rtl) ? 100 - props.fill + '%' : props.fill + '%';
    });

    const getBorderColor = computed(() => {
        if (props.roundedCorners && props.borderWidth <= 0) {
            // create a hidden border
            return (props.fill <= 0) ? props.inactiveColor : props.activeColor;
        }

        return (props.fill <= 0) ? props.borderColor : props.activeBorderColor;
    });

    const maxSize = computed(() => {
        return starPoints.value.reduce(function(a, b) {
            return Math.max(a, b);
        });
    });

    const viewBox = computed(() => {
        return '0 0 ' + maxSize.value + ' ' + maxSize.value;
    });

    const shouldAnimate = computed(() => {
        return props.animate && isStarActive.value;
    });

    const strokeLinejoin = computed(() => {
        return props.roundedCorners ? 'round' : 'miter';
    });

    function mouseMoving(e: MouseEvent) {
        if ('ontouchstart' in window) return;
        emit('star-mouse-move', {
            event: e,
            position: getPosition(e),
            id: props.starId
        });
    }

    function touchStart() {
        nextTick(() => {
            isStarActive.value = true;
        });
    }

    function touchEnd() {
        nextTick(() => {
            isStarActive.value = false;
        });
    }

    function getPosition(e: MouseEvent) {
        // calculate position in percentage.
        let starWidth = (92 / 100) * props.size;
        const offset = (props.rtl) ? Math.min(e.offsetX, 45) : Math.max(e.offsetX, 1);
        let position = Math.round((100 / starWidth) * offset);

        return Math.min(position, 100);
    }

    function selected(e: MouseEvent) {
        emit('star-selected', {
            id: props.starId,
            position: getPosition(e)
        });
    }

    function calculatePoints() {
        starPoints.value = starPoints.value.map((point, i) => {
            const offset = i % 2 === 0 ? border.value * 1.5 : 0;
            return ((props.size / maxSize.value) * point) + offset;
        });
    }

    function getColor(color: string) {
        return new AlphaColor(color).parseAlphaColor().color;
    }

    function getOpacity(color: string) {
        return new AlphaColor(color).parseAlphaColor().opacity;
    }

    starPoints.value = (props.points.length) ? [...props.points] : starPoints.value;
    calculatePoints();
    grad.value = useId();
    glowId.value = useId();
</script>

<style lang="scss" scoped>
.vue-star-rating-star {
    overflow: visible !important;
}

.vue-star-rating-star-rotate {
    transition: all .25s;
}

.vue-star-rating-star-rotate:hover {
    transition: transform 0.25s;
    transform: rotate(-15deg) scale(1.3)
}
</style>
